/**
 * What happened offline, and getting it to the server (plan.md §46).
 *
 * **Every change is a RECORD, queued on the device first.** A save, a
 * removal, a pin dropped, moved or deleted is written to IndexedDB with the
 * instant it happened and `synced: false` — that row is the queue. It drains
 * on launch, when the network comes back (`online`, and the reachability probe
 * turning true), and after every change; a failed drain retries with backoff.
 *
 * **The server merges per record, it does not replace** (backend
 * `attendance/merge.ts`). Each toggle used to PUT the whole set, so a second
 * device's older copy of the plan brought removed acts back:
 *
 *  - favourites: per act, the later of (saved at, removed at) wins — "union
 *    of adds minus later removes". A removal is a TOMBSTONE, kept on both
 *    sides, so an absence never deletes and a deliberate removal survives.
 *  - pins: last write wins per pin on `updatedAt`, a removal being a write
 *    with `deletedAt`.
 *
 * What the screens read is the plain list in `lib/storage`
 * (`myrave:{editionId}:favs` / `:pins`), synchronous so a heart is drawn on
 * the first frame. This module owns both: it writes the list whenever the
 * records change and tells every hook subscribed to that edition.
 */
import { db, type StoredFavorite, type StoredPin } from './db';
import * as apiModule from '@/lib/api';
import { readJson, writeJson } from '@/lib/storage';
import { SYNC_TIMEOUT_MS, withTimeout } from './net';
import { noteServerReached } from '@/lib/reach';
import { isFramed, PREVIEW_READ_ONLY } from '@/lib/framed';

/** No `limited`: a link that does not answer the 2.5 s probe is offline (the user, 2026-10-16). */
export type ConnectionState = 'online' | 'offline' | 'syncing';

/* ------------------------------------------------------ the display lists */

export interface LocalPin {
  /** Client-assigned, so a pin has an identity before it has ever synced. */
  id: string;
  /** 0..1 fractions of the map image, like every other point on it. */
  x: number;
  y: number;
  icon: string;
  label?: string;
}

export const DEFAULT_PIN_ICON = '⛺';

type Kind = 'favs' | 'pins';
const listeners = new Map<string, Set<() => void>>();
const topic = (editionId: string, kind: Kind) => `${editionId}\u0000${kind}`;

export function subscribe(editionId: string, kind: Kind, cb: () => void): () => void {
  const key = topic(editionId, kind);
  const set = listeners.get(key) ?? new Set();
  set.add(cb);
  listeners.set(key, set);
  return () => { set.delete(cb); };
}

function emit(editionId: string, kind: Kind): void {
  for (const cb of listeners.get(topic(editionId, kind)) ?? []) cb();
}

export function readSaved(editionId: string): string[] {
  return readJson<string[]>(editionId, 'favs', []);
}

export function writeSaved(editionId: string, slugs: string[]): void {
  writeJson(editionId, 'favs', slugs);
  emit(editionId, 'favs');
}

export function readPins(editionId: string): LocalPin[] {
  return readJson<LocalPin[]>(editionId, 'pins', []);
}

export function writePins(editionId: string, pins: LocalPin[]): void {
  writeJson(editionId, 'pins', pins);
  emit(editionId, 'pins');
}

/* ------------------------------------------------------------- the queue */

/*
 * Which records were changed on this device, and in what order — noted
 * SYNCHRONOUSLY at the change, before the IndexedDB write lands. A drain that
 * started before a change must not redraw that record from its older answer:
 * the list would lose a save made while the answer was in flight.
 */
let editClock = 0;
const edits = new Map<string, number>();
const editKey = (editionId: string, kind: Kind, id: string) => `${topic(editionId, kind)}\u0000${id}`;
const touchedSince = (editionId: string, kind: Kind, id: string, mark: number) =>
  (edits.get(editKey(editionId, kind, id)) ?? 0) > mark;

/*
 * **Whose change is it.** A queued row is tagged with the account it was made
 * as — `currentAccountId()`, which offline is the remembered account, since
 * there is no token — and a drain sends only the signed-in account's rows.
 * On a shared phone, A's unsynced favourites used to go to B's account the
 * moment B signed in. A's rows now stay on the phone, untouched and unsent,
 * until A is signed in again. A row with no owner (anonymous, or queued before
 * rows had one) is adopted by whoever drains it first, as before.
 */
function accountNow(): string | null {
  try {
    return apiModule.currentAccountId?.() ?? null;
  } catch {
    return null;
  }
}

/** The account the drain would send as — the token's, never a guess. */
function sendingAs(): string | null {
  try {
    return apiModule.sessionUserId?.() ?? null;
  } catch {
    return null;
  }
}

/** Another account's row: not this account's to send, count or show. */
function foreignTo(me: string | null, r: { userId?: string | null }): boolean {
  return me !== null && r.userId != null && r.userId !== me;
}

export async function queueFavorite(editionId: string, actSlug: string, saved: boolean): Promise<void> {
  edits.set(editKey(editionId, 'favs', actSlug), ++editClock);
  await db.favorites.put({
    id: `${editionId}:${actSlug}`,
    editionId, actSlug, saved,
    updatedAt: Date.now(),
    synced: false,
    userId: accountNow(),
  });
}

export async function queuePin(editionId: string, pin: LocalPin, deleted = false): Promise<void> {
  edits.set(editKey(editionId, 'pins', pin.id), ++editClock);
  const now = Date.now();
  await db.pins.put({
    id: `${editionId}:${pin.id}`, editionId, pinId: pin.id,
    x: pin.x, y: pin.y, icon: pin.icon, label: pin.label,
    updatedAt: now, deletedAt: deleted ? now : null, synced: false,
    userId: accountNow(),
  });
}

export async function localFavorites(editionId: string): Promise<Set<string>> {
  const rows = await db.favorites.where('editionId').equals(editionId).toArray();
  return new Set(rows.filter((r) => r.saved).map((r) => r.actSlug));
}

/** Changes still to be sent — not counting the ones the server refused, nor another account's. */
export async function unsyncedCount(editionId: string): Promise<number> {
  const me = accountNow();
  const [f, p] = await Promise.all([
    db.favorites.where('editionId').equals(editionId).filter((r) => !r.synced && !r.rejected && !foreignTo(me, r)).count(),
    db.pins.where('editionId').equals(editionId).filter((r) => !r.synced && !r.rejected && !foreignTo(me, r)).count(),
  ]);
  return f + p;
}

/** Changes the server refused: kept on this device, no longer sent. */
export async function rejectedCount(editionId: string): Promise<number> {
  const me = accountNow();
  const [f, p] = await Promise.all([
    db.favorites.where('editionId').equals(editionId).filter((r) => !r.synced && !!r.rejected && !foreignTo(me, r)).count(),
    db.pins.where('editionId').equals(editionId).filter((r) => !r.synced && !!r.rejected && !foreignTo(me, r)).count(),
  ]);
  return f + p;
}

/* ------------------------------------------------------ what can go wrong */

/** Every sync request has a clock: a hung PATCH must end, so the runner can. */
const timed = <T>(work: Promise<T>): Promise<T> => withTimeout(work, SYNC_TIMEOUT_MS);

function statusOf(err: unknown): number | null {
  try {
    return err instanceof apiModule.ApiError ? err.status : null;
  } catch {
    // A test's mock of the api module with no ApiError in it.
    const status = (err as { status?: unknown } | null)?.status;
    return typeof status === 'number' ? status : null;
  }
}

/**
 * The server ANSWERED no, and asking again will get the same no: a 4xx that
 * is not about the session (401 → the api client refreshes and retries; if it
 * still fails, that is a session to get back, so retry later) and not "slow
 * down" (408, 429). A 400 for a malformed or over-limit batch, a 403 about
 * this rave, a 404 for an edition gone.
 */
export function isPermanentRefusal(err: unknown): boolean {
  const status = statusOf(err);
  // A 403 about the ACCOUNT (not finished, not approved yet) is not about
  // this batch: it clears when the account does, and the batch goes then.
  const code = (err as { code?: unknown } | null)?.code;
  if (code === 'account_incomplete' || code === 'awaiting_approval') return false;
  // Nor is the framed preview's read-only refusal (`lib/api.ts`): it says
  // where the page is, not that these changes are wrong.
  if (code === PREVIEW_READ_ONLY) return false;
  return status !== null && status >= 400 && status < 500 && status !== 401 && status !== 408 && status !== 429;
}

/** The records of one refused batch have been set aside; not a failure to back off on. */
export class BatchRefused extends Error {
  constructor(readonly kind: Kind, readonly count: number, cause: unknown) {
    super(`the server refused ${count} ${kind} change(s)`, { cause });
    this.name = 'BatchRefused';
  }
}

/**
 * Set aside the records of a refused batch — each one only if it is still
 * exactly what was sent (a change made since is a NEW change, and is sent).
 */
async function quarantine<R extends StoredFavorite | StoredPin>(
  table: typeof db.favorites | typeof db.pins,
  sent: R[],
): Promise<void> {
  const t = table as unknown as { get(id: string): Promise<R | undefined>; put(row: R): Promise<unknown> };
  await db.transaction('rw', table, async () => {
    for (const row of sent) {
      const now = await t.get(row.id);
      if (now && !now.synced && now.updatedAt === row.updatedAt) await t.put({ ...now, rejected: true });
    }
  });
}

/* --------------------------------------------------------- the two syncs */

const iso = (ms: number) => new Date(ms).toISOString();
const ms = (s: string | undefined | null) => (s ? Date.parse(s) : 0);

interface FavoritesAnswer {
  actSlugs: string[];
  saved?: { slug: string; savedAt: string }[];
  removed?: { slug: string; removedAt: string }[];
  unknown?: string[];
}

export interface SyncResult {
  pushed: number;
  /** Records now held on this device for the edition. */
  merged: number;
}

/**
 * Push the queued favourites, and take the server's merged answer.
 *
 * Throws when the server cannot be reached, so the runner backs off; a
 * record changed WHILE the request was out is left queued for the next round
 * rather than overwritten with an answer that predates it.
 */
export async function syncFavorites(editionId: string): Promise<SyncResult> {
  /*
   * A rave that runs no `my-lineup` has no favourites API (it is a 404, §52.1).
   * Asking anyway set every queued save aside as "not accepted by the server"
   * on a rave that shows no hearts. So ask nothing: the rows stay queued, as
   * they were, and go out if the module is switched back on.
   */
  const stored = await db.bundles.get(editionId);
  if (stored && !(stored.bundle.modules ?? []).includes('my-lineup')) return { pushed: 0, merged: 0 };
  const mark = editClock;
  const me = sendingAs();
  const foreign = (r: StoredFavorite | undefined) => !!r && foreignTo(me, r);
  const rows = await db.favorites.where('editionId').equals(editionId).toArray();

  // Saves made before per-record sync exist only in the list. Adopt them with
  // the earliest possible instant: any removal the server knows of is later,
  // so a stale list cannot resurrect it, and nothing is lost that was not
  // deliberately removed somewhere.
  const known = new Set(rows.map((r) => r.actSlug));
  const adopted: StoredFavorite[] = readSaved(editionId)
    .filter((slug) => !known.has(slug))
    .map((slug) => ({ id: `${editionId}:${slug}`, editionId, actSlug: slug, saved: true, updatedAt: 0, synced: false, userId: me }));
  if (adopted.length > 0) {
    await db.favorites.bulkPut(adopted);
    rows.push(...adopted);
  }

  // Only this account's changes, and the unowned ones it adopts.
  const pending = rows.filter((r) => !r.synced && !r.rejected && !foreign(r));
  const sent = new Map(pending.map((r) => [r.actSlug, r.updatedAt]));
  let answer: FavoritesAnswer;
  try {
    answer = pending.length > 0
      ? await timed(apiModule.api.patch<FavoritesAnswer>(`/editions/${editionId}/favorites`, {
          changes: pending.map((r) => (r.saved
            ? { slug: r.actSlug, savedAt: iso(r.updatedAt) }
            : { slug: r.actSlug, removedAt: iso(r.updatedAt) })),
        }))
      : await timed(apiModule.api.get<FavoritesAnswer>(`/editions/${editionId}/favorites`));
  } catch (err) {
    if (pending.length > 0 && isPermanentRefusal(err)) {
      await quarantine(db.favorites, pending);
      throw new BatchRefused('favs', pending.length, err);
    }
    throw err;
  }

  const server = new Map<string, { saved: boolean; at: number }>();
  for (const r of answer.removed ?? []) server.set(r.slug, { saved: false, at: ms(r.removedAt) });
  if (answer.saved) for (const s of answer.saved) server.set(s.slug, { saved: true, at: ms(s.savedAt) });
  else for (const slug of answer.actSlugs ?? []) server.set(slug, { saved: true, at: 0 });

  let final: StoredFavorite[] = [];
  /** This account's saved-or-not for acts whose local row is another account's unsent change. */
  const behindForeign = new Map<string, boolean>();
  await db.transaction('rw', db.favorites, async () => {
    const now = await db.favorites.where('editionId').equals(editionId).toArray();
    const bySlug = new Map(now.map((r) => [r.actSlug, r]));
    const changedSince = (r: StoredFavorite | undefined) => !!r && !r.synced && sent.get(r.actSlug) !== r.updatedAt;

    const out: StoredFavorite[] = [];
    for (const [slug, s] of server) {
      const r = bySlug.get(slug);
      if (r && foreign(r) && !r.synced) { behindForeign.set(slug, s.saved); continue; }
      if (changedSince(r)) continue;
      out.push({ id: `${editionId}:${slug}`, editionId, actSlug: slug, saved: s.saved, updatedAt: s.at, synced: true, userId: me });
    }
    for (const r of now) {
      if (foreign(r)) continue;
      if (server.has(r.actSlug) || changedSince(r)) continue;
      // Sent and not in the answer: the act is not in this lineup (`unknown`)
      // — stop sending it. Synced and gone from the server: the server's
      // view wins for a record it once acknowledged.
      out.push(r.synced ? { ...r, saved: false, userId: me } : { ...r, synced: true, userId: me });
    }
    await db.favorites.bulkPut(out);
    final = await db.favorites.where('editionId').equals(editionId).toArray();
  });

  // What this account has saved: its own rows, and never another account's.
  const saved = new Set(final.filter((r) => r.saved && !foreign(r)).map((r) => r.actSlug));
  for (const [slug, on] of behindForeign) if (on) saved.add(slug); else saved.delete(slug);
  const before = readSaved(editionId);
  // A record changed since this drain began keeps what the list says now.
  for (const r of final) {
    if (touchedSince(editionId, 'favs', r.actSlug, mark)) {
      if (before.includes(r.actSlug)) saved.add(r.actSlug); else saved.delete(r.actSlug);
    }
  }
  for (const slug of before) if (touchedSince(editionId, 'favs', slug, mark)) saved.add(slug);
  const next = [...before.filter((s) => saved.has(s)), ...[...saved].filter((s) => !before.includes(s))];
  writeSaved(editionId, next);
  return { pushed: pending.length, merged: final.length };
}

interface PinsAnswer {
  pins: { id: string; x: number; y: number; icon?: string | null; label?: string | null; updatedAt?: string }[];
  removed?: { id: string; deletedAt: string; updatedAt?: string }[];
}

const samePlace = (a: { x: number; y: number }, b: { x: number; y: number }) =>
  Math.abs(a.x - b.x) < 1e-6 && Math.abs(a.y - b.y) < 1e-6;

/** Push the queued pins and take the server's merged answer. Same rules as the favourites. */
export async function syncPins(editionId: string): Promise<SyncResult> {
  const mark = editClock;
  const me = sendingAs();
  const foreign = (r: StoredPin | undefined) => !!r && foreignTo(me, r);
  const rows = await db.pins.where('editionId').equals(editionId).toArray();

  // Pins from before per-record sync: only in the list. One already on the
  // server (same place) IS that pin — adopting it again would duplicate it.
  const known = new Set(rows.map((r) => r.pinId));
  let orphans = readPins(editionId).filter((p) => !known.has(p.id));
  if (orphans.length > 0) {
    const remote = await timed(apiModule.api.get<PinsAnswer>(`/editions/${editionId}/pins`));
    orphans = orphans.filter((p) => !remote.pins.some((s) => samePlace(s, p)));
    const adopted: StoredPin[] = orphans.map((p) => ({
      id: `${editionId}:${p.id}`, editionId, pinId: p.id, x: p.x, y: p.y, icon: p.icon, label: p.label,
      updatedAt: 0, deletedAt: null, synced: false, userId: me,
    }));
    await db.pins.bulkPut(adopted);
    rows.push(...adopted);
  }

  // Only this account's changes, and the unowned ones it adopts.
  const pending = rows.filter((r) => !r.synced && !r.rejected && !foreign(r));
  const sent = new Map(pending.map((r) => [r.pinId, r.updatedAt]));
  let answer: PinsAnswer;
  try {
    answer = pending.length > 0
      ? await timed(apiModule.api.patch<PinsAnswer>(`/editions/${editionId}/pins`, {
          changes: pending.map((r) => ({
            id: r.pinId, x: r.x, y: r.y, icon: r.icon || undefined, label: r.label || undefined,
            updatedAt: iso(r.updatedAt), ...(r.deletedAt ? { deletedAt: iso(r.deletedAt) } : {}),
          })),
        }))
      : await timed(apiModule.api.get<PinsAnswer>(`/editions/${editionId}/pins`));
  } catch (err) {
    if (pending.length > 0 && isPermanentRefusal(err)) {
      await quarantine(db.pins, pending);
      throw new BatchRefused('pins', pending.length, err);
    }
    throw err;
  }

  const server = new Map<string, StoredPin>();
  for (const r of answer.removed ?? []) {
    const at = ms(r.updatedAt ?? r.deletedAt);
    server.set(r.id, {
      id: `${editionId}:${r.id}`, editionId, pinId: r.id, x: 0, y: 0, updatedAt: at, deletedAt: ms(r.deletedAt), synced: true, userId: me,
    });
  }
  for (const p of answer.pins ?? []) {
    server.set(p.id, {
      id: `${editionId}:${p.id}`, editionId, pinId: p.id, x: p.x, y: p.y,
      icon: p.icon || DEFAULT_PIN_ICON, label: p.label || undefined,
      updatedAt: ms(p.updatedAt), deletedAt: null, synced: true, userId: me,
    });
  }

  let final: StoredPin[] = [];
  await db.transaction('rw', db.pins, async () => {
    const now = await db.pins.where('editionId').equals(editionId).toArray();
    const byId = new Map(now.map((r) => [r.pinId, r]));
    const changedSince = (r: StoredPin | undefined) => !!r && !r.synced && sent.get(r.pinId) !== r.updatedAt;
    const out: StoredPin[] = [];
    for (const [id, s] of server) {
      const r = byId.get(id);
      if (r && foreign(r) && !r.synced) continue;
      if (changedSince(r)) continue;
      // A tombstone from the server keeps the last known place, for nothing.
      out.push(s.deletedAt && r ? { ...r, deletedAt: s.deletedAt, updatedAt: s.updatedAt, synced: true, userId: me } : s);
    }
    for (const r of now) {
      if (foreign(r)) continue;
      if (server.has(r.pinId) || changedSince(r)) continue;
      out.push(r.synced ? { ...r, deletedAt: r.deletedAt ?? Date.now(), userId: me } : { ...r, synced: true, userId: me });
    }
    await db.pins.bulkPut(out);
    final = await db.pins.where('editionId').equals(editionId).toArray();
  });

  const live = new Map(final.filter((r) => !r.deletedAt && !foreign(r)).map((r) => [r.pinId, r]));
  const before = readPins(editionId);
  const toPin = (r: StoredPin): LocalPin => ({ id: r.pinId, x: r.x, y: r.y, icon: r.icon || DEFAULT_PIN_ICON, label: r.label || undefined });
  const fresh = (id: string) => touchedSince(editionId, 'pins', id, mark);
  const next = [
    // A pin changed since this drain began stays as the list has it now.
    ...before.filter((p) => fresh(p.id) || live.has(p.id)).map((p) => (fresh(p.id) ? p : toPin(live.get(p.id)!))),
    ...[...live.values()].filter((r) => !fresh(r.pinId) && !before.some((p) => p.id === r.pinId)).map(toPin),
  ];
  writePins(editionId, next);
  return { pushed: pending.length, merged: final.length };
}

/* ------------------------------------------------------------ the runner */

export interface SyncStatus {
  syncing: boolean;
  pending: number;
  /** Changes the server refused, kept on this device only (`rejected`). */
  rejected: number;
  /** Consecutive failed drains; 0 after a success. */
  failures: number;
}

interface Runner {
  running: Promise<void> | null;
  again: boolean;
  /** Stopped for want of a session; drained when one arrives (`onSession`). */
  waiting: boolean;
  failures: number;
  timer: ReturnType<typeof setTimeout> | null;
  status: SyncStatus;
  listeners: Set<(s: SyncStatus) => void>;
}

const runners = new Map<string, Runner>();

function runnerFor(editionId: string): Runner {
  let r = runners.get(editionId);
  if (!r) {
    r = { running: null, again: false, waiting: false, failures: 0, timer: null, status: { syncing: false, pending: 0, rejected: 0, failures: 0 }, listeners: new Set() };
    runners.set(editionId, r);
    listenForSession();
  }
  return r;
}

/*
 * An offline launch has no access token, so the first drain stops at "not
 * signed in" — and used to stay stopped: nothing asked again. Whenever a
 * refresh brings a session (the network back, the page visible again, a 401
 * retried), every runner that stopped for want of one drains.
 */
let listening = false;
function listenForSession(): void {
  if (listening) return;
  listening = true;
  try {
    apiModule.onSession((session) => {
      if (!session) return;
      for (const [editionId, r] of runners) {
        if (!r.waiting) continue;
        r.waiting = false;
        void requestSync(editionId);
      }
    });
  } catch {
    // A test's mock of the api module with no session events in it.
  }
}

function setStatus(editionId: string, patch: Partial<SyncStatus>): void {
  const r = runnerFor(editionId);
  r.status = { ...r.status, ...patch };
  for (const cb of r.listeners) cb(r.status);
}

export function syncStatus(editionId: string): SyncStatus {
  return runnerFor(editionId).status;
}

export function onSyncStatus(editionId: string, cb: (s: SyncStatus) => void): () => void {
  const r = runnerFor(editionId);
  r.listeners.add(cb);
  return () => { r.listeners.delete(cb); };
}

/** First retry after 2s, doubling to at most five minutes. */
export function backoffMs(failures: number): number {
  return Math.min(2000 * 2 ** Math.max(0, failures - 1), 5 * 60_000);
}

/** Whether a session exists — with no account there is nothing to sync to. */
function signedIn(): boolean {
  try {
    return apiModule.hasSession();
  } catch {
    return false;
  }
}

async function refreshPending(editionId: string): Promise<void> {
  try {
    const [pending, rejected] = await Promise.all([unsyncedCount(editionId), rejectedCount(editionId)]);
    setStatus(editionId, { pending, rejected });
  } catch {
    // IndexedDB unavailable (a private window): nothing is queued either.
  }
}

/**
 * Drain this edition's queue now, or as soon as the drain in flight ends.
 *
 * Never loops on its own: a second run happens only because something ASKED
 * during the first (a change made mid-sync), and a failure schedules ONE
 * retry on the backoff clock instead of spinning. The old `syncNow` set
 * 'online' in a `finally` that re-fired the effect that called it — and when
 * the sync returned early with work still queued, it spun.
 */
export function requestSync(editionId: string): Promise<void> {
  // The setup wizard's framed preview sends nothing (round 7, point 51): this
  // device's queued changes wait for the real app, untouched.
  if (isFramed()) return Promise.resolve();
  const r = runnerFor(editionId);
  if (r.running) {
    r.again = true;
    return r.running;
  }
  if (r.timer) {
    clearTimeout(r.timer);
    r.timer = null;
  }
  r.running = (async () => {
    do {
      r.again = false;
      if (!signedIn()) { r.waiting = true; break; }
      setStatus(editionId, { syncing: true });
      /*
       * Favourites and pins drain INDEPENDENTLY. One sequence meant a refused
       * favourites batch stopped every pin behind it, for good.
       */
      let failed = false;
      for (const drain of [syncFavorites, syncPins]) {
        try {
          await drain(editionId);
        } catch (err) {
          // Refused for good: the batch is set aside (or there was none to
          // set aside, and asking again gets the same no). Not a retry.
          if (err instanceof BatchRefused || isPermanentRefusal(err)) continue;
          failed = true;
        }
      }
      if (failed) {
        r.failures++;
        const delay = backoffMs(r.failures);
        r.timer = setTimeout(() => { r.timer = null; void requestSync(editionId); }, delay);
        break;
      }
      r.failures = 0;
    } while (r.again);
  })().finally(async () => {
    r.running = null;
    setStatus(editionId, { syncing: false, failures: r.failures });
    await refreshPending(editionId);
  });
  return r.running;
}

/** Record a change's arrival in the queue, and start draining it. */
export function changed(editionId: string): void {
  void refreshPending(editionId);
  void requestSync(editionId);
}

/** Test support: wait until no drain is in flight (a late one would write into the next test). */
export async function syncIdleForTests(): Promise<void> {
  for (let i = 0; i < 20; i++) {
    await new Promise((r) => setTimeout(r, 0));
    const running = [...runners.values()].map((r) => r.running).filter(Boolean);
    if (running.length === 0) return;
    await Promise.allSettled(running);
  }
}

/** Test support: forget every runner's timers and state. */
export function resetSyncForTests(): void {
  for (const r of runners.values()) if (r.timer) clearTimeout(r.timer);
  runners.clear();
  edits.clear();
}

/* ---------------------------------------------------------- reachability */

/**
 * Is the network actually usable?
 *
 * `navigator.onLine` only means "an interface is up". A phone joined to
 * festival wifi with no upstream reports online and reaches nothing, which is
 * precisely the situation this app exists to survive.
 */
export async function isReachable(apiBase: string, timeoutMs = 2500): Promise<boolean> {
  if (!navigator.onLine) return false;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(`${apiBase}/health`, { signal: controller.signal, cache: 'no-store' });
    noteServerReached();
    return res.ok;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}
